import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ModelViewerWithToggle from './ModelViewerWithToggle';

function ProjectDetails() {
  const { projectNumber } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjectDetails();
  }, [projectNumber]);

  const fetchProjectDetails = async () => {
    try {
      const response = await fetch(`/api/projects/${projectNumber}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Project not found');
        } else {
          setError('Error loading project details');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setProject(data);
      setLoading(false);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading project details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/client-viewer')}
          >
            Back to Project Lookup
          </Button>
        </Box>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8 }}>
          <Alert severity="warning">Project not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/client-viewer')}
            sx={{ mt: 2 }}
          >
            Back to Project Lookup
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/client-viewer')}
          sx={{ mb: 2 }}
        >
          Back to Lookup
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Project: {project.projectNumber}
            </Typography>
            <Chip
              label={project.status || 'Unknown'}
              color={getStatusColor(project.status)}
              sx={{ mr: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Client: {project.clientName} | Created: {new Date(project.createdDate).toLocaleDateString()}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom>
                Project Description
              </Typography>
              <Typography variant="body1" paragraph>
                {project.description || 'No description available'}
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
                Specifications
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>Material</strong>
                      </TableCell>
                      <TableCell>{project.material || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>Dimensions</strong>
                      </TableCell>
                      <TableCell>{project.dimensions || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>Finish</strong>
                      </TableCell>
                      <TableCell>{project.finish || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>Estimated Completion</strong>
                      </TableCell>
                      <TableCell>
                        {project.estimatedCompletion
                          ? new Date(project.estimatedCompletion).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom>
                Quote Summary
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(project.quoteItems || []).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          ${item.unitPrice?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell align="right">
                          ${item.total?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!project.quoteItems || project.quoteItems.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No quote items available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">
                  Total: ${project.totalAmount?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {project.model3dUrl && (
            <>
              <Divider sx={{ my: 4 }} />
              <ModelViewerWithToggle modelUrl={project.model3dUrl} modelRotation={project.modelRotation} modelDebug={project.modelDebug} />
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default ProjectDetails;
